import { useEffect, useMemo, useState, FormEvent } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";
import queryString from "query-string";
import { useQueryClient } from "@tanstack/react-query";
import { compareAsc, addDays } from "date-fns";

import {
  SubmissionModel,
  TransactionModel,
  PrivateUseModel,
  AssetsPrivateUseModel,
  QuestionnaireModel,
  FileModel,
} from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  QUERY_WIDTHS,
  PER_PAGE_VALUES,
  DAYS_BEFORE_END_SUBMISSION_PERIOD,
} from "@utils/index";
import { attachmentsService } from "@services/index";
import {
  useSubmissionGetOne,
  useTransactionGetAll,
  useTransactionGetOne,
  useTransactionDelete,
  useSubmissionSubmitData,
  usePrivateUseGetAll,
  usePrivateUseCreate,
  usePrivateUseUpdate,
  useQuestionnaireGetObj,
  useQuestionnaireUpdateAnswer,
  useQuestionnaireAppendFiles,
  useQuestionnaireDeleteFile,
  useQuestionnaireSubmit,
} from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  PageContentContainer,
  TableTabs,
  MobileHeader,
  ChevronLeftSvg,
  Button,
  ChecksSvg,
  SubmissionStatus,
  SubmissionKpiPanel,
  TutorialButton,
  TabTransactions,
  TabQuestionnaire,
  TabPrivateUse,
  PrivateUseInputData,
  EditMotorConfirmModal,
  PrivateUseReviewModal,
  SubmitConfirmModal,
  DeleteTransactionConfirmModal,
  AssetDocsNeededModal,
  SubmitQuestionnaireConfirmModal,
} from "@components/index";
import { TransactionDetails } from "@pages/index";

export const SubmissionDetails = ({
  isPrivateUse,
}: {
  isPrivateUse?: boolean;
}) => {
  const location = useLocation();
  const idValue = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const puEnabled = useUserStore(({ puEnabled }) => puEnabled);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const queryClient = useQueryClient();

  const [submissionDetails, setSubmissionDetails] = useState<SubmissionModel>();
  const [questionnaireObj, setQuestionnaireObj] =
    useState<QuestionnaireModel>();
  const [questionnaireStatus, setQuestionnaireStatus] =
    useState<QuestionnaireModel["status"]>();
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<
    Record<string, string>
  >({});
  const [isUploadingQuestionnaireFile, setIsUploadingQuestionnaireFile] =
    useState(false);
  const [activeTab, setActiveTab] = useState("");

  const [transactions, setTransactions] = useState<TransactionModel[]>();

  const [privateUses, setPrivateUses] = useState<PrivateUseModel[]>();
  const [privateUseTotal, setPrivateUseTotal] = useState<string>();
  const [privateUseTotalBusinessExpenses, setPrivateUseBusinessExpensesTotal] =
    useState<string>();
  const [privateUseTotalPrivateExpenses, setPrivateUsePrivateExpensesTotal] =
    useState<string>();
  const [inputValue, setInputValue] = useState("");
  const [inputData, setInputData] = useState<PrivateUseInputData>({});
  const [confirmEditMotor, setConfirmEditMotor] = useState(false);

  const [selectedPage, setSelectedPage] = useState(1);
  const [countTransactions, setCountTransactions] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE_VALUES[0]);

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [transactionId, setTransactionId] = useState("");
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionModel>();
  const [removeId, setRemoveId] = useState("");

  const [modalPrivateUse, setModalPrivateUse] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [isConfirmedAgree, setIsConfirmedAgree] = useState(false);

  const [isSubmittingBeforePeriodEnd, setIsSubmittingBeforePeriodEnd] =
    useState(false);

  const [modalNeedsAssetDocs, setModalNeedsAssetDocs] = useState<string[]>([]);
  const [confirmSubmitQuestionnaire, setConfirmSubmitQuestionnaire] =
    useState(false);

  useEffect(() => {
    const params = queryString.parse(location.search);
    setSelectedPage(parseInt((params?.page || "1").toString()));
    setPerPage((params?.perPage as string) || PER_PAGE_VALUES[0]);
    setActiveTab((params?.activeTab as string) || "Transactions");
  }, [location]);

  const pageTitle = useMemo(
    () =>
      submissionDetails
        ? `${submissionDetails.period.title} : ${submissionDetails.userBusiness.name}`
        : "",
    [submissionDetails],
  );

  const submitIsDisabled = useMemo(
    () =>
      !["open", "reopened"].includes(submissionDetails?.status?.value || ""),
    [submissionDetails],
  );

  const submitQuestionnarieIsDisabled = useMemo(
    () =>
      !questionnaireObj?.questions.length ||
      questionnaireObj.questions.some(
        ({ uniqueId, attachmentRequirement, attachments }) =>
          !questionnaireAnswers[uniqueId] ||
          (attachmentRequirement === "required" && !attachments?.length),
      ),
    [questionnaireObj, questionnaireAnswers],
  );

  const tabs = useMemo(() => {
    const arr = ["Transactions"];
    if (
      puEnabled &&
      !disabledFeatures.includes("Private Use") &&
      submissionDetails?.userBusiness.type !== "landlord"
    ) {
      arr.push("Private use log");
    }
    if (questionnaireObj?.linked) {
      arr.push("Questionnaire");
    }
    return arr;
  }, [questionnaireObj, submissionDetails, puEnabled, disabledFeatures]);

  const {
    isPending: isPendingGetOne,
    isSuccess: isSuccessGetOne,
    isStale: isStaleGetOne,
    data: dataGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
  } = useSubmissionGetOne(idValue);

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  useEffect(() => {
    if (isSuccessGetOne && dataGetOne) {
      setSubmissionDetails(dataGetOne);
    }
  }, [isSuccessGetOne, dataGetOne]);

  const {
    isPending: isPendingQuestionnaireGetObj,
    isSuccess: isSuccessQuestionnaireGetObj,
    isStale: isStaleQuestionnaireGetObj,
    data: dataQuestionnaireGetObj,
    isError: isErrorQuestionnaireGetObj,
    error: errorQuestionnaireGetObj,
  } = useQuestionnaireGetObj(idValue);

  useEffect(() => {
    if (isErrorQuestionnaireGetObj && errorQuestionnaireGetObj) {
      notify("error", errorQuestionnaireGetObj.message);
    }
  }, [isErrorQuestionnaireGetObj, errorQuestionnaireGetObj, notify]);

  useEffect(() => {
    if (isSuccessQuestionnaireGetObj && dataQuestionnaireGetObj) {
      setQuestionnaireObj(dataQuestionnaireGetObj);
    }
  }, [isSuccessQuestionnaireGetObj, dataQuestionnaireGetObj]);

  useEffect(() => {
    if (!questionnaireObj?.questions.length) {
      return;
    }
    setQuestionnaireStatus(questionnaireObj.status);
    setQuestionnaireAnswers(
      questionnaireObj.questions.reduce(
        (acc, { uniqueId, answer }) => ({ ...acc, [uniqueId]: answer }),
        {},
      ),
    );
  }, [questionnaireObj]);

  const {
    isPending: isPendingGetTransactions,
    isStale: isStaleGetTransactions,
    isSuccess: isSuccessGetTransactions,
    data: dataGetTransactions,
    isError: isErrorGetTransactions,
    error: errorGetTransactions,
  } = useTransactionGetAll({
    submissionId: submissionDetails?.uniqueId,
    page: selectedPage,
    perPage,
  });

  useEffect(() => {
    if (isSuccessGetTransactions && dataGetTransactions) {
      setTransactions(dataGetTransactions.transactions);
      setCountTransactions(dataGetTransactions.pagination.total);
    }
  }, [isSuccessGetTransactions, dataGetTransactions]);

  useEffect(() => {
    if (isErrorGetTransactions && errorGetTransactions) {
      notify("error", errorGetTransactions.message);
    }
  }, [isErrorGetTransactions, errorGetTransactions, notify]);

  const {
    isPending: isPendingGetPrivateUses,
    isStale: isStaleGetPrivateUses,
    isSuccess: isSuccessGetPrivateUses,
    data: dataGetPrivateUses,
    isError: isErrorGetPrivateUses,
    error: errorGetPrivateUses,
  } = usePrivateUseGetAll({
    puEnabled: puEnabled && !disabledFeatures.includes("Private Use"),
    businessId: submissionDetails?.userBusiness.uniqueId,
    submissionId: submissionDetails?.uniqueId,
  });

  useEffect(() => {
    if (isSuccessGetPrivateUses && dataGetPrivateUses) {
      setPrivateUses(dataGetPrivateUses.items);
      setPrivateUseTotal(dataGetPrivateUses.total);
      setPrivateUseBusinessExpensesTotal(
        dataGetPrivateUses.totalBusinessExpenses,
      );
      setPrivateUsePrivateExpensesTotal(
        dataGetPrivateUses.totalPrivateExpenses,
      );
    }
  }, [isSuccessGetPrivateUses, dataGetPrivateUses]);

  useEffect(() => {
    if (isErrorGetPrivateUses && errorGetPrivateUses) {
      notify("error", errorGetPrivateUses.message);
    }
  }, [isErrorGetPrivateUses, errorGetPrivateUses, notify]);

  const {
    isPending: isPendingTransactionDetails,
    isSuccess: isSuccessTransactionDetails,
    isStale: isStaleTransactionDetails,
    data: dataTransactionDetails,
    isError: isErrorTransactionDetails,
    error: errorTransactionDetails,
  } = useTransactionGetOne(transactionId);

  useEffect(() => {
    if (isErrorTransactionDetails && errorTransactionDetails) {
      notify("error", errorTransactionDetails.message);
    }
  }, [isErrorTransactionDetails, errorTransactionDetails, notify]);

  useEffect(() => {
    if (isSuccessTransactionDetails && dataTransactionDetails) {
      setTransactionDetails(dataTransactionDetails);
    }
  }, [isSuccessTransactionDetails, dataTransactionDetails]);

  const {
    mutate: mutateDeleteTransaction,
    isPending: isPendingDeleteTransaction,
  } = useTransactionDelete(
    () => {
      notify("success", "Your Transaction has been successfully deleted.");
      handleCloseDetails();
      setRemoveId("");
      queryClient.invalidateQueries({
        queryKey: ["submissionGetOne", idValue],
      });
      queryClient.invalidateQueries({ queryKey: ["transactionGetAll"] });
      queryClient.invalidateQueries({ queryKey: ["privateUseGetAll"] });
    },
    ({ message }) => notify("error", message),
  );

  const { mutate: mutateSubmitData, isPending: isPendingSubmitData } =
    useSubmissionSubmitData(
      () => {
        queryClient.invalidateQueries({
          queryKey: ["submissionGetOne", idValue],
        });
        queryClient.invalidateQueries({ queryKey: ["transactionGetAll"] });
        notify("success", "Submission Successful & in Review.");
      },
      ({ message, fields }) => {
        if (fields && message.includes("Please upload the")) {
          const keys = Object.keys(fields);
          setModalNeedsAssetDocs(keys.map((key) => fields[key]));
          return;
        }
        notify("error", message);
      },
    );

  const {
    mutate: mutateCreatePrivateUse,
    isPending: isPendingCreatePrivateUse,
  } = usePrivateUseCreate(
    () => {
      notify("success", "Private use created successfully.");
      queryClient.invalidateQueries({
        queryKey: [
          "privateUseGetAll",
          submissionDetails?.userBusiness.uniqueId,
          submissionDetails?.uniqueId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["submissionGetOne", idValue],
      });
    },
    ({ message }) => notify("error", message),
  );

  const {
    mutate: mutateUpdatePrivateUse,
    isPending: isPendingUpdatePrivateUse,
  } = usePrivateUseUpdate(
    () => {
      notify("success", "Private use updated successfully.");
      queryClient.invalidateQueries({
        queryKey: [
          "privateUseGetAll",
          submissionDetails?.userBusiness.uniqueId,
          submissionDetails?.uniqueId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["submissionGetOne", idValue],
      });
    },
    ({ message }) => notify("error", message),
  );

  const {
    mutate: mutateQuestionnaireUpdateAnswer,
    isPending: isPendingQuestionnaireUpdateAnswer,
  } = useQuestionnaireUpdateAnswer(
    () => {
      queryClient.invalidateQueries({
        queryKey: ["questionnaireGetObj", idValue],
      });
    },
    ({ message }) => notify("error", message),
  );

  const {
    mutate: mutateQuestionnaireUpdateFiles,
    isPending: isPendingQuestionnaireUpdateFiles,
  } = useQuestionnaireAppendFiles(
    () => {
      queryClient.invalidateQueries({
        queryKey: ["questionnaireGetObj", idValue],
      });
    },
    ({ message }) => notify("error", message),
  );

  const {
    mutate: mutateQuestionnaireDeleteFile,
    isPending: isPendingQuestionnaireDeleteFile,
  } = useQuestionnaireDeleteFile(
    () => {
      queryClient.invalidateQueries({
        queryKey: ["questionnaireGetObj", idValue],
      });
    },
    ({ message }) => notify("error", message),
  );

  const {
    mutate: mutateQuestionnaireSubmit,
    isPending: isPendingQuestionnaireSubmit,
  } = useQuestionnaireSubmit(
    () => {
      queryClient.invalidateQueries({
        queryKey: ["questionnaireGetObj", idValue],
      });
    },
    ({ message }) => notify("error", message),
  );

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetOne && isPendingGetOne) ||
        (isStaleQuestionnaireGetObj && isPendingQuestionnaireGetObj) ||
        (isStaleGetTransactions && isPendingGetTransactions) ||
        (isStaleGetPrivateUses && isPendingGetPrivateUses) ||
        (isStaleTransactionDetails && isPendingTransactionDetails) ||
        isPendingSubmitData ||
        isPendingDeleteTransaction ||
        isPendingCreatePrivateUse ||
        isPendingUpdatePrivateUse ||
        isPendingQuestionnaireUpdateAnswer ||
        isPendingQuestionnaireUpdateFiles ||
        isPendingQuestionnaireDeleteFile ||
        isUploadingQuestionnaireFile ||
        isPendingQuestionnaireSubmit,
    });
  }, [
    isStaleGetOne,
    isPendingGetOne,
    isStaleQuestionnaireGetObj,
    isPendingQuestionnaireGetObj,
    isStaleGetTransactions,
    isPendingGetTransactions,
    isStaleGetPrivateUses,
    isPendingGetPrivateUses,
    isStaleTransactionDetails,
    isPendingTransactionDetails,
    isPendingSubmitData,
    isPendingDeleteTransaction,
    isPendingCreatePrivateUse,
    isPendingUpdatePrivateUse,
    isPendingQuestionnaireUpdateAnswer,
    isPendingQuestionnaireUpdateFiles,
    isPendingQuestionnaireDeleteFile,
    isUploadingQuestionnaireFile,
    isPendingQuestionnaireSubmit,
    setState,
  ]);

  useEffect(() => {
    if (
      puEnabled &&
      !disabledFeatures.includes("Private Use") &&
      !!activeTab &&
      !!privateUses?.length
    ) {
      setState({ isDomUpdated: true });
    }
  }, [puEnabled, disabledFeatures, activeTab, privateUses, setState]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      { page: selectedPage, perPage, activeTab },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [redirectNeeds, navigate, selectedPage, perPage, activeTab]);

  useEffect(() => {
    const today = new Date();
    const endPeriodDate = submissionDetails
      ? addDays(
          new Date(submissionDetails.period.endDate),
          -DAYS_BEFORE_END_SUBMISSION_PERIOD,
        )
      : undefined;
    setIsSubmittingBeforePeriodEnd(
      !!(
        isConfirmedAgree &&
        endPeriodDate &&
        compareAsc(today, endPeriodDate) < 0
      ),
    );
  }, [isConfirmedAgree, submissionDetails]);

  const handleSubmitData = () => {
    setComment("");
    setIsConfirmedAgree(false);
    mutateSubmitData({
      submissionId: submissionDetails!.uniqueId,
      comment,
    });
    setConfirmSubmitting(false);
  };

  const handleCancelSubmitData = () => {
    setComment("");
    setIsConfirmedAgree(false);
    setConfirmSubmitting(false);
  };

  const handleCloseDetails = () => {
    setTransactionId("");
    setTransactionDetails(undefined);
  };

  const handleSelectTab = (value: string) => {
    setRedirectNeeds(true);
    setActiveTab(value);
  };

  const handleSelectPage = (page: number) => {
    setRedirectNeeds(true);
    setSelectedPage(page);
  };

  const handleSelectPerPage = (value: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setPerPage(value);
  };

  const saveInputValue = () => {
    if (inputData.privateUseId) {
      mutateUpdatePrivateUse({
        privateUseId: inputData.privateUseId.toString(),
        percentage: inputValue || "0",
      });
    } else {
      mutateCreatePrivateUse({
        businessId: submissionDetails!.userBusiness.uniqueId,
        submissionId: submissionDetails!.uniqueId,
        assetId: inputData.assetId!,
        categoryId: inputData.categoryId!,
        percentage: inputValue || "0",
      });
    }
    setInputValue("");
    setInputData({});
  };

  const onPrivateUseFormSubmit = (
    e: FormEvent<HTMLFormElement>,
    asset: AssetsPrivateUseModel,
  ) => {
    e.preventDefault();
    if (
      inputData.categoryTitle === "Motor" &&
      asset.mileagePrivateUse &&
      asset.mileagePrivateUse > 0
    ) {
      setConfirmEditMotor(true);
    } else {
      saveInputValue();
    }
  };

  const cancelConfirmEditMotor = () => {
    setConfirmEditMotor(false);
    setInputValue("");
    setInputData({});
  };

  const handleChangeQuestionnaireAnswer = (uniqueId: string, value: string) => {
    setQuestionnaireAnswers({ ...questionnaireAnswers, [uniqueId]: value });
  };

  const handleSelectQuestionnaireFiles = async (
    target: HTMLInputElement,
    uniqueId: string,
  ) => {
    setIsUploadingQuestionnaireFile(true);
    if (!target.files?.length) {
      setIsUploadingQuestionnaireFile(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    setIsUploadingQuestionnaireFile(false);

    if (typeof newFiles === "string") {
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index]?.size || 0,
    }));

    mutateQuestionnaireUpdateFiles({
      submissionId: submissionDetails!.uniqueId,
      questionId: uniqueId,
      files: newFilesWithSizes,
    });
  };

  const removeQuestionnaireAttachment = (
    attachmentId: string,
    uniqueId: string,
  ) => {
    mutateQuestionnaireDeleteFile({
      submissionId: submissionDetails!.uniqueId,
      questionId: uniqueId,
      fileId: attachmentId,
    });
  };

  const handleSaveQuestionnaireAnswer = (uniqueId: string) => {
    setQuestionnaireStatus("partially_completed");
    mutateQuestionnaireUpdateAnswer({
      submissionId: submissionDetails!.uniqueId,
      questionId: uniqueId,
      answerText: questionnaireAnswers[uniqueId],
    });
  };

  const handleSubmitQuestionnaire = () => {
    setQuestionnaireStatus("completed");
    mutateQuestionnaireSubmit({
      submissionId: submissionDetails!.uniqueId,
    });
  };

  return (
    <div className="bg-lightGray">
      {isMobile && <MobileHeader href="/submissions" title={pageTitle} />}
      <div className="flex flex-col justify-between px-4 pt-4 md:flex-row md:pr-8 md:pt-6">
        <div className="flex flex-col gap-y-4 md:gap-y-6">
          {!isMobile ? (
            <div className="flex items-center gap-x-2">
              <Link
                to={isPrivateUse ? "/privateUse" : "/submissions"}
                className="flex h-8 w-8 items-center justify-center rounded-e-lg border border-black-8 bg-white-100 text-primary-300"
              >
                <ChevronLeftSvg />
              </Link>
              <h1 className="flex items-center gap-x-4 text-600 font-bold text-black-100">
                {pageTitle}
                {submissionDetails?.status && (
                  <SubmissionStatus status={submissionDetails.status} />
                )}
              </h1>
            </div>
          ) : (
            <div>
              {submissionDetails?.status && (
                <SubmissionStatus status={submissionDetails.status} />
              )}
            </div>
          )}
          <SubmissionKpiPanel
            submissionDetails={submissionDetails}
            puEnabled={puEnabled}
            disabledFeatures={disabledFeatures}
          />
        </div>
        <div className="mt-6 flex items-end md:mt-0">
          <Button
            dataTestid="submitButton_submissionDetails"
            title="Submit this data"
            height="small"
            disabled={submitIsDisabled}
            leftIcon={<ChecksSvg />}
            className={clsx(
              "w-full bg-secondary-100 text-white-100 hover:opacity-80",
              submitIsDisabled && "opacity-60",
            )}
            onClick={() => {
              if (
                puEnabled &&
                !disabledFeatures.includes("Private Use") &&
                submissionDetails?.userBusiness.type !== "landlord"
              ) {
                setModalPrivateUse(true);
              } else {
                setConfirmSubmitting(true);
              }
            }}
          />
        </div>
      </div>
      <PageContentContainer>
        <TableTabs
          tabs={tabs}
          activeTab={activeTab}
          onClick={(value) => handleSelectTab(value)}
        />

        {activeTab === "Transactions" && (
          <TabTransactions
            transactions={transactions}
            countTransactions={countTransactions}
            disabledFeatures={disabledFeatures}
            isMobile={isMobile}
            selectedPage={selectedPage}
            perPage={perPage}
            onSelectPage={handleSelectPage}
            onSelectPerPage={handleSelectPerPage}
            onDetails={setTransactionId}
            onRemove={setRemoveId}
          />
        )}

        {activeTab === "Private use log" &&
          (privateUses?.length ? (
            <>
              <div className="py-4 md:pb-2">
                <TutorialButton
                  variant="dark"
                  tutorialNames={["privateUseSimpleCreateTutorial"]}
                  faqCategory={["Private Use"]}
                />
              </div>
              <TabPrivateUse
                privateUses={privateUses}
                privateUseTotal={privateUseTotal}
                privateUseTotalPrivateExpenses={privateUseTotalPrivateExpenses}
                privateUseTotalBusinessExpenses={
                  privateUseTotalBusinessExpenses
                }
                disabledFeatures={disabledFeatures}
                isMobile={isMobile}
                submitIsDisabled={submitIsDisabled}
                inputData={inputData}
                inputValue={inputValue}
                setInputData={setInputData}
                setInputValue={setInputValue}
                onFormSubmit={onPrivateUseFormSubmit}
              />
            </>
          ) : (
            <div className="pb-4 pt-12 text-center text-400">
              No private uses found
            </div>
          ))}

        {activeTab === "Questionnaire" && questionnaireObj && (
          <TabQuestionnaire
            questionnaireObj={questionnaireObj}
            questionnaireStatus={questionnaireStatus}
            questionnaireAnswers={questionnaireAnswers}
            submissionDetails={submissionDetails}
            isPendingQuestionnaireUpdateAnswer={
              isPendingQuestionnaireUpdateAnswer
            }
            submitQuestionnarieIsDisabled={submitQuestionnarieIsDisabled}
            onChangeAnswer={handleChangeQuestionnaireAnswer}
            onSaveAnswer={handleSaveQuestionnaireAnswer}
            onSelectFiles={handleSelectQuestionnaireFiles}
            onRemoveAttachment={removeQuestionnaireAttachment}
            onSubmitClick={() => setConfirmSubmitQuestionnaire(true)}
          />
        )}
      </PageContentContainer>
      <TransactionDetails
        transaction={transactionDetails}
        onRemove={() => mutateDeleteTransaction(transactionDetails!.uniqueId)}
        onClose={handleCloseDetails}
      />
      <EditMotorConfirmModal
        show={confirmEditMotor}
        onClickCancel={cancelConfirmEditMotor}
        onClickConfirm={() => {
          saveInputValue();
          setConfirmEditMotor(false);
        }}
      />
      <PrivateUseReviewModal
        show={modalPrivateUse}
        privateUses={privateUses}
        onClose={() => setModalPrivateUse(false)}
        onConfirm={() => {
          setModalPrivateUse(false);
          setConfirmSubmitting(true);
        }}
        onEdit={() => {
          setModalPrivateUse(false);
          setActiveTab("Private use log");
        }}
      />
      <SubmitConfirmModal
        show={confirmSubmitting}
        submissionDetails={submissionDetails}
        isSubmittingBeforePeriodEnd={isSubmittingBeforePeriodEnd}
        comment={comment}
        disabledConfirmButton={!isConfirmedAgree}
        onCommentChange={setComment}
        onToggleConfirmedAgree={() => setIsConfirmedAgree(!isConfirmedAgree)}
        onClickCancel={handleCancelSubmitData}
        onClickConfirm={handleSubmitData}
      />
      <DeleteTransactionConfirmModal
        show={!!removeId}
        onClickCancel={() => setRemoveId("")}
        onClickConfirm={() => mutateDeleteTransaction(removeId)}
      />
      <AssetDocsNeededModal
        messages={modalNeedsAssetDocs}
        onClose={() => setModalNeedsAssetDocs([])}
      />
      <SubmitQuestionnaireConfirmModal
        show={confirmSubmitQuestionnaire}
        onClickCancel={() => setConfirmSubmitQuestionnaire(false)}
        onClickConfirm={() => {
          handleSubmitQuestionnaire();
          setConfirmSubmitQuestionnaire(false);
        }}
      />
    </div>
  );
};
